import React, { useEffect, useState, useRef } from 'react';
import { MessageSquareIcon, XIcon, SendIcon, BotIcon, UserIcon } from 'lucide-react';
interface Message {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}
export function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    content: "Hello! I'm your mortgage assistant. How can I help you today?",
    sender: 'bot',
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Mock AI response based on user input
  const generateBotResponse = (userMessage: string): string => {
    const lowerCaseMessage = userMessage.toLowerCase();
    if (lowerCaseMessage.includes('hello') || lowerCaseMessage.includes('hi')) {
      return 'Hello! How can I assist you with your mortgage needs today?';
    } else if (lowerCaseMessage.includes('rate') || lowerCaseMessage.includes('interest')) {
      return 'Our current mortgage rates range from 3.5% to 4.2% depending on your credit score, loan term, and down payment. Would you like me to provide more specific information based on your situation?';
    } else if (lowerCaseMessage.includes('document') || lowerCaseMessage.includes('upload')) {
      return 'You can upload documents by going to the Applications > Documents section. We accept PDFs, JPGs, and PNGs. Is there a specific document you need to upload?';
    } else if (lowerCaseMessage.includes('pre-approval') || lowerCaseMessage.includes('preapproval')) {
      return "To get pre-approved, we'll need your income verification, credit history, and details about your assets. Would you like me to guide you through the pre-approval process?";
    } else if (lowerCaseMessage.includes('contact') || lowerCaseMessage.includes('speak') || lowerCaseMessage.includes('human')) {
      return "If you'd like to speak with a mortgage specialist, please call (555) 123-4567 during business hours, or I can schedule a callback for you. Would you like me to arrange that?";
    } else {
      return "Thank you for your message. I'm still learning how to respond to different questions. Is there something specific about mortgages, applications, or document requirements that I can help with?";
    }
  };
  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;
    const newUserMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages([...messages, newUserMessage]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI thinking and responding
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: generateBotResponse(inputValue),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000); // Random delay between 1-2 seconds
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="fixed bottom-5 left-5 z-50" data-id="element-552">
      {/* Chat Button */}
      <button onClick={toggleChat} className={`flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 transition-all ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`} aria-label="Open chat" data-id="element-553">
        <MessageSquareIcon className="h-6 w-6" data-id="element-554" />
      </button>
      {/* Chat Window */}
      <div className={`bg-white rounded-lg shadow-xl flex flex-col transition-all duration-300 overflow-hidden ${isOpen ? 'opacity-100 scale-100 w-80 h-[450px]' : 'opacity-0 scale-0 w-0 h-0'}`} data-id="element-555">
        {/* Chat Header */}
        <div className="bg-primary-600 text-white p-4 flex items-center justify-between" data-id="element-556">
          <div className="flex items-center" data-id="element-557">
            <BotIcon className="h-5 w-5 mr-2" data-id="element-558" />
            <h3 className="font-medium" data-id="element-559">Mortgage Assistant</h3>
          </div>
          <button onClick={toggleChat} className="text-white hover:text-neutral-200 transition-colors" aria-label="Close chat" data-id="element-560">
            <XIcon className="h-5 w-5" data-id="element-561" />
          </button>
        </div>
        {/* Chat Messages */}
        <div className="flex-1 p-4 overflow-y-auto bg-neutral-50" data-id="element-562">
          {messages.map(message => <div key={message.id} className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-563">
              <div className={`max-w-[80%] rounded-lg p-3 ${message.sender === 'user' ? 'bg-primary-500 text-white' : 'bg-white text-neutral-800 border border-neutral-200'}`} data-id="element-564">
                <div className="flex items-center mb-1" data-id="element-565">
                  {message.sender === 'bot' ? <BotIcon className="h-4 w-4 mr-1 text-primary-600" data-id="element-566" /> : <UserIcon className="h-4 w-4 mr-1" data-id="element-567" />}
                  <span className="text-xs opacity-75" data-id="element-568">
                    {formatTime(message.timestamp)}
                  </span>
                </div>
                <p className="text-sm" data-id="element-569">{message.content}</p>
              </div>
            </div>)}
          {isTyping && <div className="flex justify-start mb-4" data-id="element-570">
              <div className="max-w-[80%] rounded-lg p-3 bg-white text-neutral-800 border border-neutral-200" data-id="element-571">
                <div className="flex items-center" data-id="element-572">
                  <BotIcon className="h-4 w-4 mr-2 text-primary-600" data-id="element-573" />
                  <div className="typing-indicator" data-id="element-574">
                    <span data-id="element-575"></span>
                    <span data-id="element-576"></span>
                    <span data-id="element-577"></span>
                  </div>
                </div>
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-578" />
        </div>
        {/* Chat Input */}
        <div className="p-3 border-t border-neutral-200 bg-white" data-id="element-579">
          <div className="flex items-center" data-id="element-580">
            <input type="text" value={inputValue} onChange={handleInputChange} onKeyPress={handleKeyPress} placeholder="Type your message..." className="flex-1 border border-neutral-300 rounded-l-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" data-id="element-581" />
            <button onClick={handleSendMessage} disabled={inputValue.trim() === ''} className="bg-primary-600 text-white rounded-r-md px-3 py-2 text-sm hover:bg-primary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed" data-id="element-582">
              <SendIcon className="h-4 w-4" data-id="element-583" />
            </button>
          </div>
        </div>
      </div>
      {/* CSS for typing indicator */}
      <style jsx data-id="element-584">{`
        .typing-indicator {
          display: flex;
          align-items: center;
        }
        .typing-indicator span {
          height: 8px;
          width: 8px;
          margin: 0 1px;
          background-color: #6b7280;
          border-radius: 50%;
          display: inline-block;
          opacity: 0.4;
          animation: typing 1.4s infinite ease-in-out both;
        }
        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typing {
          0% {
            transform: scale(1);
            opacity: 0.4;
          }
          20% {
            transform: scale(1.5);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 0.4;
          }
        }
      `}</style>
    </div>;
}